import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Layers,
  Radar,
  Shield,
  Sparkles,
  Zap,
} from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '../lib/utils';
import { LegalFooter } from '../components/LegalFooter';

const ICONS = { integrate: Layers, analyze: Radar, predict: Sparkles, protect: Shield };

export function PlatformOverviewPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const j = await api.get('/api/public/platform');
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setErr(e.message || 'Failed to load platform data');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const product = data?.product;
  const pillars = data?.pillars ?? [];
  const valueProps = data?.value_props ?? [];
  const verticals = data?.verticals ?? [];
  const flags = data?.flags ?? {};
  const disclaimer = data?.disclaimer;
  const documentationUrl = data?.documentation_url;

  return (
    <div className="min-h-screen bg-[#050508] text-foreground">
      <div className="border-b border-white/10 bg-zinc-950/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-6 py-4">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to sign in
          </Link>
          <span className="font-heading text-lg font-bold text-white">AERTA</span>
        </div>
      </div>

      <div className="mx-auto max-w-5xl space-y-16 px-6 py-12">
        <header className="space-y-4 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-400/90">Platform overview</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-white md:text-5xl">
            {product?.short_name ?? 'AERTA'}
          </h1>
          <p className="text-lg text-zinc-400">{product?.acronym}</p>
          <p className="mx-auto max-w-3xl text-lg leading-relaxed text-zinc-300">{product?.tagline}</p>
          {product?.positioning && (
            <ul className="mx-auto mt-6 max-w-2xl space-y-2 text-left text-sm text-zinc-400">
              {product.positioning.map((line) => (
                <li key={line} className="flex gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500/80" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          )}
        </header>

        {err && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
            {err} — showing structure from last successful load only if you refresh after starting the API.
          </div>
        )}

        <section>
          <h2 className="mb-2 font-heading text-2xl font-semibold text-white">Four steps to stronger validation</h2>
          <p className="mb-8 max-w-3xl text-sm text-zinc-500">
            Integrate → Analyze → Predict → Protect — mapped to capabilities shipped in this product. For a detailed
            capability matrix or pilot pack, use materials shared by your AERTA contact
            {documentationUrl ? (
              <>
                {' '}
                or see{' '}
                <a
                  href={documentationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sky-400/90 underline-offset-4 hover:underline"
                >
                  online documentation
                </a>
              </>
            ) : null}
            .
          </p>
          <div className="grid gap-6 md:grid-cols-2">
            {pillars.map((p) => {
              const Icon = ICONS[p.id] || Zap;
              return (
                <div
                  key={p.id}
                  className="rounded-2xl border border-white/10 bg-zinc-950/60 p-6 shadow-xl shadow-black/40"
                >
                  <div className="mb-3 flex items-center gap-2 text-primary">
                    <Icon className="h-5 w-5" />
                    <span className="font-heading text-lg font-semibold text-white">{p.title}</span>
                  </div>
                  <p className="font-medium text-zinc-200">{p.headline}</p>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-400">{p.body}</p>
                  {p.product_surfaces?.length > 0 && (
                    <ul className="mt-4 space-y-2 border-t border-white/10 pt-4 text-sm">
                      {p.product_surfaces.map((s) => (
                        <li key={s.label} className="flex flex-wrap items-center gap-2 text-zinc-500">
                          <span className="text-zinc-300">{s.label}</span>
                          {s.route && (
                            <Link to={s.route} className="text-primary hover:underline">
                              Open
                            </Link>
                          )}
                          {s.api && (
                            <code className="rounded bg-zinc-900 px-1.5 py-0.5 text-[11px] text-zinc-500">{s.api}</code>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <h2 className="mb-6 font-heading text-2xl font-semibold text-white">What AERTA emphasizes</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {valueProps.map((v) => (
              <div
                key={v.title}
                className="rounded-xl border border-white/5 bg-white/[0.03] px-4 py-3 backdrop-blur-sm"
              >
                <p className="font-medium text-zinc-100">{v.title}</p>
                <p className="mt-1 text-sm text-zinc-500">{v.detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="mb-6 font-heading text-2xl font-semibold text-white">Use-case alignment</h2>
          <p className="mb-6 max-w-3xl text-sm text-zinc-500">
            Vertical narratives describe where the platform fits; certifications and production guarantees remain with
            your organization and deployment model.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {verticals.map((v) => (
              <div key={v.id} className="rounded-xl border border-white/10 bg-zinc-950/40 p-5">
                <h3 className="font-heading font-semibold text-white">{v.title}</h3>
                <p className="mt-2 text-sm text-zinc-400">{v.summary}</p>
                <p className={cn('mt-3 border-t border-white/10 pt-3 text-xs text-zinc-500')}>{v.note}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-2xl border border-white/10 bg-zinc-900/50 p-6">
          <h2 className="font-heading text-lg font-semibold text-white">Runtime flags (this deployment)</h2>
          <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
            {Object.entries(flags).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 border-b border-white/5 py-2">
                <dt className="font-mono text-xs text-zinc-500">{k}</dt>
                <dd className="text-zinc-300">{String(v)}</dd>
              </div>
            ))}
          </dl>
        </section>

        {disclaimer && (
          <p className="border-t border-white/10 pt-8 text-center text-xs leading-relaxed text-zinc-600">{disclaimer}</p>
        )}

        <div className="border-t border-white/10 pt-8">
          <LegalFooter className="[&_a]:text-zinc-500 [&_a:hover]:text-primary" />
        </div>
      </div>
    </div>
  );
}
